import { useLayoutEffect } from "preact/hooks";

/**
 * Below this, the gap between the layout viewport and the visual one is the
 * browser's own furniture — a collapsing URL bar, a floating toolbar — not a
 * keyboard. Reacting to those would have the launcher drifting up and down
 * the screen as the shopper scrolls the host page, which is worse than the
 * problem being solved here.
 */
const KEYBOARD_MIN_INSET_PX = 150;

export interface ViewportMetrics {
  /** `visualViewport.height` — the part of the page the shopper can actually see. */
  height: number;
  /** How far the visual viewport has slid down the layout one; non-zero on iOS when the page scrolls under a focused field. */
  offsetTop: number;
  /** How much of the layout viewport an on-screen keyboard covers, or 0 when none is up. */
  keyboardInset: number;
}

/** The two numbers the browser gives us, turned into the three the CSS wants. Pure, so the threshold above is testable without a phone. */
export function measureViewport(viewport: { height: number; offsetTop: number }, layoutHeight: number): ViewportMetrics {
  const covered = layoutHeight - viewport.height - viewport.offsetTop;
  return {
    height: viewport.height,
    offsetTop: viewport.offsetTop,
    keyboardInset: covered >= KEYBOARD_MIN_INSET_PX ? covered : 0,
  };
}

/**
 * Writes the metrics onto `.cos-root` as custom properties, which is where
 * `styles.ts` reads them from. Every one of them has a fallback in the CSS,
 * so a browser without `visualViewport` — or inline mode, where the host
 * page owns the layout and we never measure at all — gets the geometry the
 * widget always had.
 */
export function applyViewportMetrics(root: HTMLElement, metrics: ViewportMetrics): void {
  root.style.setProperty("--cos-viewport-height", `${Math.round(metrics.height)}px`);
  root.style.setProperty("--cos-viewport-offset-top", `${Math.round(metrics.offsetTop)}px`);
  root.style.setProperty("--cos-keyboard-inset", `${Math.round(metrics.keyboardInset)}px`);
  // Exposed as an attribute as well as a length: "is a keyboard up" is a
  // question a layout rule may want to ask directly, and `calc()` cannot.
  if (metrics.keyboardInset > 0) root.dataset.keyboard = "open";
  else delete root.dataset.keyboard;
}

/**
 * Keeps the widget inside the part of the screen the shopper can see.
 *
 * iOS does not resize the page when its keyboard opens: `100vh`,
 * `window.innerHeight` and every `position: fixed` offset go on describing a
 * viewport whose bottom third is now behind the keys. The visual viewport is
 * the only thing that tells the truth about it, so the panel is sized from
 * that instead. Android's default (`interactive-widget=resizes-content`)
 * shrinks the layout viewport itself, which makes the measured inset ~0 —
 * correctly, since there is nothing left to correct for.
 */
export function useVisualViewport(ref: { current: HTMLElement | null }, enabled = true): void {
  useLayoutEffect(() => {
    if (!enabled) return;
    const viewport = typeof window === "undefined" ? undefined : window.visualViewport;
    if (!viewport) return;

    // iOS fires these continuously through the keyboard animation, and each
    // one writes three custom properties that invalidate the panel's layout
    // — one write per frame is all that can be shown anyway.
    let frame: number | undefined;
    function sync() {
      frame = undefined;
      const root = ref.current;
      if (root && viewport) applyViewportMetrics(root, measureViewport(viewport, window.innerHeight));
    }
    function schedule() {
      if (frame !== undefined) return;
      frame = typeof requestAnimationFrame === "function" ? requestAnimationFrame(sync) : undefined;
      if (frame === undefined) sync();
    }

    sync();
    viewport.addEventListener("resize", schedule);
    viewport.addEventListener("scroll", schedule);
    return () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
      viewport.removeEventListener("resize", schedule);
      viewport.removeEventListener("scroll", schedule);
    };
  }, [ref, enabled]);
}
